import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { MarkdownView } from './MarkdownView';

/**
 * PoliciesLibrary — Admin management of the org's policy pack.
 *
 * Renders the 19 default templates alongside any org-created custom
 * policies. Admins can:
 *   - One-click seed all defaults (idempotent)
 *   - Edit body markdown + title on any policy
 *   - Toggle published / draft (parents only see published)
 *   - Open the printable HTML view
 *
 * Mount inside Org Settings on the Admin dashboard.
 */
const CATEGORY_ORDER = ['Commercial', 'Safeguarding', 'Welfare', 'Health & Safety', 'Governance'];

const statusBadge = (row) => {
  if (!row.exists) return { label: 'Not created', bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)' };
  if (row.published) return { label: `Published · v${row.version || 1}`, bg: 'hsl(140 60% 92%)', fg: 'hsl(140 45% 26%)' };
  return { label: 'Draft', bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)' };
};

export function PoliciesLibrary({ onAuditChange } = {}) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [bulkResetting, setBulkResetting] = useState(false);
  const [editing, setEditing] = useState(null); // {slug, title, body_markdown, published}
  const [saving, setSaving] = useState(false);
  // Custom-policy creation modal state.
  const [creatingCustom, setCreatingCustom] = useState(false);
  const [customDraft, setCustomDraft] = useState({ title: '', category: 'Custom', body_markdown: '', ofsted_reference: '—' });
  // Acknowledgment log state — lazy-loaded when the panel is opened.
  const [ackOpen, setAckOpen] = useState(false);
  const [ackLoading, setAckLoading] = useState(false);
  const [ackReport, setAckReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.adminListPolicies();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const c = r.category || 'Other';
      (g[c] = g[c] || []).push(r);
    }
    return g;
  }, [rows]);

  // Count policies that were auto-seeded but not yet reviewed/tailored
  // by an admin — used to render the "review recommended" banner at
  // the top of the library so nobody publishes generic template text
  // to parents without at least a skim.
  const autoSeededPendingReview = useMemo(
    () => rows.filter((r) => r.exists && r.auto_seeded === true).length,
    [rows],
  );

  // Aggregate placeholder audit: how many policies still contain
  // unresolved `[Placeholder]` / `{{token}}` markers. Surfaces at the
  // top of the library so admins know they need to fill Organisation
  // Settings *and* re-render the affected policies.
  const placeholderPolicyCount = useMemo(
    () => rows.filter((r) => r.exists && r.has_placeholders).length,
    [rows],
  );
  const placeholderTotalTokens = useMemo(
    () => rows.reduce((n, r) => n + (r.exists && r.has_placeholders ? (r.placeholder_count || 0) : 0), 0),
    [rows],
  );

  // Publish the aggregate placeholder count upward whenever it
  // changes. Lets the AdminDashboard render a red pill on the
  // "Public Presence" sub-tab without needing to fetch policies
  // twice. Guarded so components mounted without the callback stay
  // functional.
  useEffect(() => {
    if (typeof onAuditChange === 'function') {
      onAuditChange(placeholderPolicyCount);
    }
  }, [placeholderPolicyCount, onAuditChange]);

  const orderedCategories = useMemo(() => {
    const known = CATEGORY_ORDER.filter((c) => grouped[c]);
    const extras = Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c));
    return [...known, ...extras];
  }, [grouped]);

  const seedAll = async () => {
    setSeeding(true);
    try {
      const res = await api.adminSeedPolicies();
      const inserted = (res && res.inserted && res.inserted.length) || 0;
      toast.success(inserted > 0
        ? `Seeded ${inserted} default polic${inserted === 1 ? 'y' : 'ies'}.`
        : 'All defaults already exist — nothing to do.'
      );
      await load();
    } catch (err) {
      toast.error(err?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const openEditor = (row) => {
    // If the row hasn't been created yet, we still open the editor with
    // the registry defaults — the backend PUT auto-seeds the row.
    setEditing({
      slug: row.slug,
      title: row.title || '',
      body_markdown: row.body_markdown || '',
      published: !!row.published,
      _existing: !!row.exists,
    });
  };

  const saveEditor = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const updated = await api.adminUpdatePolicy(editing.slug, {
        title: editing.title,
        body_markdown: editing.body_markdown,
        published: editing.published,
      });
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.slug === editing.slug);
        const merged = { ...(prev[idx] || {}), ...updated, exists: true };
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = merged;
          return next;
        }
        return [merged, ...prev];
      });
      toast.success(editing.published ? 'Policy saved and published.' : 'Policy saved as draft.');
      setEditing(null);
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (row) => {
    try {
      const updated = await api.adminUpdatePolicy(row.slug, { published: !row.published });
      setRows((prev) => prev.map((r) => (r.slug === row.slug ? { ...r, ...updated, exists: true } : r)));
      toast.success(updated.published ? 'Policy published — visible to parents.' : 'Policy unpublished — hidden from parents.');
    } catch (err) {
      toast.error(err?.message || 'Update failed');
    }
  };

  // Re-render a default policy from its shipped template using the
  // org's *current* Organisation Settings. Wipes out unresolved
  // placeholder tokens (e.g. [Registered Manager]) once the admin has
  // filled those fields. Confirms first because it replaces any
  // hand-tailored edits with the fresh template.
  const resetPolicy = async (row) => {
    if (row.is_custom) {
      toast.error('Custom policies have no template to reset to.');
      return;
    }
    const ok = window.confirm(
      `Reset "${row.title}" to the shipped template?\n\nThis re-renders the body using your current Organisation Settings (Registered Manager, Ofsted URN, DSL, LADO phone, etc.) and REPLACES any hand-tailored edits. This action cannot be undone.`
    );
    if (!ok) return;
    try {
      const res = await api.adminResetPolicy(row.slug);
      const updated = (res && res.policy) || {};
      setRows((prev) => prev.map((r) => (r.slug === row.slug
        ? {
            ...r,
            ...updated,
            exists: true,
            has_placeholders: false,
            placeholder_count: 0,
            placeholders_found: [],
          }
        : r
      )));
      // Refresh once so backend-side re-audit takes over (in case
      // Organisation Settings still has some missing fields).
      load();
      toast.success('Policy reset — placeholder tokens re-checked against current Organisation Settings.');
    } catch (err) {
      toast.error(err?.message || 'Reset failed');
    }
  };

  // Bulk-reset every default policy that currently contains an
  // unresolved placeholder. Confirms first because it overwrites any
  // hand-tailored edits on the affected policies. Runs one server
  // call — the endpoint iterates on the backend to keep it atomic
  // and cheap on network.
  const bulkResetPlaceholderPolicies = async () => {
    if (placeholderPolicyCount === 0) return;
    const ok = window.confirm(
      `Reset ${placeholderPolicyCount} polic${placeholderPolicyCount === 1 ? 'y' : 'ies'} with unresolved placeholders?\n\n`
      + 'Every affected default policy will be re-rendered from its shipped template using your current Organisation Settings. '
      + 'ANY HAND-TAILORED EDITS ON THESE POLICIES WILL BE REPLACED. This action cannot be undone.\n\n'
      + 'Custom policies (with no shipped template) are skipped.'
    );
    if (!ok) return;
    setBulkResetting(true);
    try {
      const res = await api.adminResetAllPoliciesWithPlaceholders();
      const resetCount = res?.reset_count || 0;
      const stillCount = res?.still_has_placeholders_count || 0;
      if (resetCount === 0) {
        toast.info('Nothing to reset — no default policies currently contain placeholders.');
      } else if (stillCount > 0) {
        toast.warning(
          `Reset ${resetCount} polic${resetCount === 1 ? 'y' : 'ies'}, but ${stillCount} still contain placeholders. `
          + 'Fill the remaining fields in Organisation Settings and click Reset all again.'
        );
      } else {
        toast.success(`Reset ${resetCount} polic${resetCount === 1 ? 'y' : 'ies'} — all placeholder tokens cleared.`);
      }
      await load();
    } catch (err) {
      toast.error(err?.message || 'Bulk reset failed');
    } finally {
      setBulkResetting(false);
    }
  };

  const submitCustom = async () => {
    const t = (customDraft.title || '').trim();
    const b = (customDraft.body_markdown || '').trim();
    if (t.length < 3) { toast.error('Title must be at least 3 characters'); return; }
    if (b.length < 1) { toast.error('Body cannot be empty'); return; }
    setSaving(true);
    try {
      const created = await api.adminCreateCustomPolicy({
        title: t,
        category: (customDraft.category || 'Custom').trim() || 'Custom',
        body_markdown: b,
        ofsted_reference: (customDraft.ofsted_reference || '—').trim() || '—',
      });
      // Insert into the rows list (marked as existing) and immediately
      // open the editor so the admin can publish/tweak.
      setRows((prev) => [{ ...created, exists: true }, ...prev]);
      setCreatingCustom(false);
      setCustomDraft({ title: '', category: 'Custom', body_markdown: '', ofsted_reference: '—' });
      toast.success('Custom policy created as draft. Click Publish when ready.');
    } catch (err) {
      toast.error(err?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteCustom = async (row) => {
    if (!row.is_custom) {
      toast.error('Only custom policies can be deleted. Default policies can be unpublished instead.');
      return;
    }
    if (!window.confirm(`Delete "${row.title}"?\n\nThis also removes any parent acknowledgments for this policy. This cannot be undone.`)) return;
    try {
      await api.adminDeletePolicy(row.slug);
      setRows((prev) => prev.filter((r) => r.slug !== row.slug));
      toast.success('Custom policy deleted.');
    } catch (err) {
      toast.error(err?.message || 'Delete failed');
    }
  };

  const loadAckReport = useCallback(async () => {
    setAckLoading(true);
    try {
      const report = await api.adminGetAcknowledgmentLog();
      setAckReport(report);
    } catch (err) {
      toast.error(err?.message || 'Failed to load acknowledgment log');
    } finally {
      setAckLoading(false);
    }
  }, []);

  const toggleAckPanel = () => {
    const next = !ackOpen;
    setAckOpen(next);
    if (next && !ackReport) loadAckReport();
  };

  return (
    <div className="card" data-testid="policies-library-card">
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0 }}>📚 Policies Library</h2>
          <small style={{ color: 'hsl(var(--muted-foreground))' }}>
            EYFS/Ofsted policy pack for your setting. Parents see <strong>Published</strong> ones on their Policies tab.
          </small>
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={toggleAckPanel}
            data-testid="policies-ack-panel-toggle"
            title="See which parents have acknowledged each published policy"
          >
            {ackOpen ? '📊 Hide log' : '📊 Sign-off log'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setCreatingCustom(true)}
            data-testid="policies-new-custom"
            title="Add a policy that isn't in the default Ofsted pack"
          >
            ➕ New custom policy
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={seedAll}
            disabled={seeding}
            data-testid="policies-seed-all"
          >
            {seeding ? 'Generating…' : '🪄 Generate default policies'}
          </button>
        </div>
      </div>

      {/* Review-recommended banner: appears when any policy is still
          flagged auto_seeded=true (i.e. was seeded from the template
          registry and never edited by an admin). We do NOT block the
          admin — the policies are already published and usable — we
          just nudge them to review the templated wording. Editing any
          policy body clears the flag for that policy. */}
      {autoSeededPendingReview > 0 && (
        <div
          data-testid="policies-review-banner"
          style={{
            marginTop: '0.75rem',
            padding: '0.65rem 0.85rem',
            background: 'hsl(38 95% 96%)',
            border: '1px solid hsl(38 90% 78%)',
            borderRadius: 8,
            color: 'hsl(30 75% 25%)',
            fontSize: '0.85rem',
          }}
        >
          <strong>🟡 {autoSeededPendingReview} polic{autoSeededPendingReview === 1 ? 'y' : 'ies'} auto-generated from Ofsted-aligned templates.</strong>{' '}
          They are already <strong>published</strong> so parents can see them, but please review and tailor each one to your setting (open times, fees, named safeguarding lead, etc.). Editing a policy will clear this note for that one.
        </div>
      )}

      {/* Placeholder audit banner — appears when ≥1 published or draft
          policy still contains unresolved template placeholders (either
          {{key}} tokens or [Human Label] sentinel fallbacks emitted
          when an Organisation Settings field is blank). Nudges the
          admin toward Organisation Settings → the correct sub-tab. */}
      {placeholderPolicyCount > 0 && (
        <div
          data-testid="policies-placeholder-banner"
          style={{
            marginTop: '0.75rem',
            padding: '0.65rem 0.85rem',
            background: 'hsl(0 78% 96%)',
            border: '1px solid hsl(0 78% 82%)',
            borderRadius: 8,
            color: 'hsl(0 70% 28%)',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <strong>🔴 {placeholderPolicyCount} polic{placeholderPolicyCount === 1 ? 'y' : 'ies'} still contain{placeholderPolicyCount === 1 ? 's' : ''} unresolved placeholders ({placeholderTotalTokens} token{placeholderTotalTokens === 1 ? '' : 's'} total).</strong>{' '}
            Open <strong>Organisation Settings → Profile / Public Presence / Preferences</strong>, fill the missing fields (Registered Manager, Ofsted URN, DSL, LADO phone, notice period, etc.), then click <strong>Reset all</strong> below (or Reset on each policy) to re-render with your real data.
          </div>
          <button
            type="button"
            onClick={bulkResetPlaceholderPolicies}
            disabled={bulkResetting}
            data-testid="policies-placeholder-bulk-reset"
            title={`Re-render all ${placeholderPolicyCount} affected default policies from their shipped templates using your current Organisation Settings. Overwrites any hand-tailored edits on those policies.`}
            style={{
              flex: '0 0 auto',
              alignSelf: 'center',
              padding: '0.45rem 0.85rem',
              background: bulkResetting ? 'hsl(0 60% 82%)' : 'hsl(0 70% 45%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '0.82rem',
              fontWeight: 700,
              cursor: bulkResetting ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              boxShadow: '0 1px 2px hsl(0 40% 30% / 0.15)',
              transition: 'transform 120ms ease, filter 120ms ease',
            }}
          >
            {bulkResetting
              ? '⏳ Resetting…'
              : `🔄 Reset all ${placeholderPolicyCount}`}
          </button>
        </div>
      )}

      {/* Sign-off log panel — collapsed by default, lazy-loaded. */}
      {ackOpen && (
        <div
          style={{
            marginTop: '0.75rem',
            padding: '0.75rem 0.85rem',
            border: '1px solid hsl(var(--border))',
            borderRadius: 'var(--radius)',
            background: 'hsl(var(--muted) / 0.2)',
          }}
          data-testid="policies-ack-panel"
        >
          {ackLoading ? (
            <div className="loading" data-testid="policies-ack-loading">Loading sign-off log…</div>
          ) : !ackReport ? (
            <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
              No data yet — refresh once policies are published.
            </div>
          ) : ackReport.policies.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }} data-testid="policies-ack-empty">
              Publish at least one policy to start collecting parent acknowledgments.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                <span data-testid="policies-ack-summary" style={{ fontSize: '0.85rem' }}>
                  Overall coverage: <strong>{ackReport.summary.overall_coverage_pct}%</strong>
                  {' · '}
                  <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {ackReport.summary.fully_acknowledged} / {ackReport.summary.total_parents} parents fully signed off
                  </span>
                </span>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', marginLeft: 'auto' }}
                  onClick={loadAckReport}
                  data-testid="policies-ack-refresh"
                >
                  🔄 Refresh
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }} data-testid="policies-ack-table">
                  <thead>
                    <tr style={{ borderBottom: '2px solid hsl(var(--border))', textAlign: 'left' }}>
                      <th style={{ padding: '0.35rem 0.4rem' }}>Policy</th>
                      <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>Signed (current)</th>
                      <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>Stale (old version)</th>
                      <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>Not yet</th>
                      <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>Coverage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ackReport.policies.map((p) => (
                      <tr key={p.slug} style={{ borderTop: '1px solid hsl(var(--border))' }} data-testid={`policies-ack-row-${p.slug}`}>
                        <td style={{ padding: '0.35rem 0.4rem' }}>{p.title} <small style={{ color: 'hsl(var(--muted-foreground))' }}>v{p.current_version}</small></td>
                        <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.acknowledged_current}</td>
                        <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.acknowledged_stale > 0 ? 'hsl(35 80% 35%)' : undefined }}>{p.acknowledged_stale}</td>
                        <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.not_yet_acknowledged > 0 ? 'hsl(0 60% 40%)' : undefined }}>{p.not_yet_acknowledged}</td>
                        <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{p.coverage_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading" data-testid="policies-library-loading" style={{ padding: '1rem 0' }}>Loading policies…</div>
      ) : (
        <div style={{ marginTop: '0.75rem' }}>
          {orderedCategories.map((cat) => (
            <div key={cat} style={{ marginBottom: '1.25rem' }} data-testid={`policies-category-${cat}`}>
              <h3 style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'hsl(var(--muted-foreground))', margin: '0.5rem 0' }}>
                {cat}
              </h3>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {grouped[cat].map((row) => {
                  const badge = statusBadge(row);
                  return (
                    <div
                      key={row.slug}
                      data-testid={`policy-row-${row.slug}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto',
                        gap: '0.75rem',
                        alignItems: 'center',
                        padding: '0.75rem 0.85rem',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 'var(--radius)',
                        background: 'hsl(var(--card))',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{row.title}</div>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>
                          {row.ofsted_reference && row.ofsted_reference !== '—' ? `${row.ofsted_reference} · ` : ''}
                          <span
                            style={{
                              display: 'inline-block',
                              background: badge.bg, color: badge.fg,
                              fontSize: '0.7rem', padding: '0.1rem 0.5rem',
                              borderRadius: 999, fontWeight: 600,
                            }}
                            data-testid={`policy-status-${row.slug}`}
                          >
                            {badge.label}
                          </span>
                          {row.exists && row.auto_seeded === true && (
                            <span
                              style={{
                                display: 'inline-block',
                                marginLeft: 6,
                                background: 'hsl(38 95% 92%)',
                                color: 'hsl(30 80% 30%)',
                                fontSize: '0.65rem',
                                padding: '0.1rem 0.45rem',
                                borderRadius: 999,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                              }}
                              title="Auto-generated from an Ofsted-aligned template. Please review and tailor to your setting."
                              data-testid={`policy-review-${row.slug}`}
                            >
                              🟡 Review
                            </span>
                          )}
                          {row.exists && row.has_placeholders && (
                            <span
                              style={{
                                display: 'inline-block',
                                marginLeft: 6,
                                background: 'hsl(0 78% 94%)',
                                color: 'hsl(0 70% 32%)',
                                fontSize: '0.65rem',
                                padding: '0.1rem 0.5rem',
                                borderRadius: 999,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                                border: '1px solid hsl(0 78% 82%)',
                              }}
                              title={
                                'This policy still contains unresolved placeholders — fill the matching Organisation Settings fields and click "Reset to template" on this policy to re-render.\n\nDetected: '
                                + (Array.isArray(row.placeholders_found) ? row.placeholders_found.join(', ') : '')
                                + (row.placeholder_count > (row.placeholders_found?.length || 0)
                                  ? ` (+${row.placeholder_count - (row.placeholders_found?.length || 0)} more)`
                                  : '')
                              }
                              data-testid={`policy-placeholders-${row.slug}`}
                            >
                              🔴 {row.placeholder_count} placeholder{row.placeholder_count === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        {row.exists && row.published && (
                          <a
                            className="btn btn-ghost"
                            href={api.getPolicyPrintableUrl(row.slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                            data-testid={`policy-view-${row.slug}`}
                          >
                            📄 View
                          </a>
                        )}
                        {row.exists && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => togglePublish(row)}
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                            data-testid={`policy-publish-${row.slug}`}
                            title={row.published ? 'Currently published. Click to hide from parents.' : 'Currently a draft. Click to publish.'}
                          >
                            {row.published ? '🔒 Unpublish' : '🚀 Publish'}
                          </button>
                        )}
                        {row.exists && !row.is_custom && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => resetPolicy(row)}
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                            data-testid={`policy-reset-${row.slug}`}
                            title="Re-render this policy from the shipped Ofsted template using your current Organisation Settings. Replaces any hand-tailored edits."
                          >
                            🔄 Reset
                          </button>
                        )}
                        {row.exists && row.is_custom && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => deleteCustom(row)}
                            style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', color: 'hsl(0 60% 40%)' }}
                            data-testid={`policy-delete-${row.slug}`}
                            title="Delete this custom policy (default policies cannot be deleted)"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => openEditor(row)}
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                        data-testid={`policy-edit-${row.slug}`}
                      >
                        {row.exists ? '✏️ Edit' : '➕ Generate & edit'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="modal-overlay" onClick={() => (saving ? null : setEditing(null))} data-testid="policy-editor-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 960, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
              <div style={{ flex: 1, minWidth: 0, marginRight: '0.5rem' }}>
                <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Title</label>
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))}
                  data-testid="policy-editor-title"
                  style={{ width: '100%', fontSize: '1.1rem', fontWeight: 600 }}
                />
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)} disabled={saving} data-testid="policy-editor-close">✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', maxHeight: '58vh' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                  Markdown source · <em>supports # heading, **bold**, *italic*, - list, &gt; quote, ---</em>
                </label>
                <textarea
                  value={editing.body_markdown}
                  onChange={(e) => setEditing((s) => ({ ...s, body_markdown: e.target.value }))}
                  data-testid="policy-editor-body"
                  style={{
                    flex: 1, width: '100%',
                    minHeight: 380,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                    resize: 'vertical',
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                  Live preview
                </label>
                <div
                  data-testid="policy-editor-preview"
                  style={{
                    flex: 1,
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    padding: '0.75rem 0.9rem',
                    background: 'hsl(var(--muted) / 0.2)',
                    overflowY: 'auto',
                    minHeight: 380,
                    maxHeight: '58vh',
                  }}
                >
                  <MarkdownView source={editing.body_markdown} testId="policy-editor-preview-content" />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.85rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }} data-testid="policy-editor-published-toggle">
                <input
                  type="checkbox"
                  checked={!!editing.published}
                  onChange={(e) => setEditing((s) => ({ ...s, published: e.target.checked }))}
                  disabled={saving}
                />
                Published (visible to parents)
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)} disabled={saving} data-testid="policy-editor-cancel">
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={saveEditor} disabled={saving} data-testid="policy-editor-save">
                  {saving ? 'Saving…' : 'Save policy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {creatingCustom && (
        <div className="modal-overlay" onClick={() => (saving ? null : setCreatingCustom(false))} data-testid="custom-policy-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
              <div>
                <h2 style={{ margin: 0 }}>➕ New custom policy</h2>
                <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                  Add a policy that's specific to your setting (e.g. Uniform, Forest School Kit, Homework).
                </small>
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => setCreatingCustom(false)} disabled={saving} data-testid="custom-policy-close">✕</button>
            </div>

            <div style={{ display: 'grid', gap: '0.6rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Title <span style={{ color: 'hsl(0 60% 40%)' }}>*</span></label>
                <input
                  type="text"
                  value={customDraft.title}
                  onChange={(e) => setCustomDraft((s) => ({ ...s, title: e.target.value }))}
                  placeholder="e.g. Uniform Policy"
                  maxLength={200}
                  data-testid="custom-policy-title"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Category</label>
                  <select
                    value={customDraft.category}
                    onChange={(e) => setCustomDraft((s) => ({ ...s, category: e.target.value }))}
                    data-testid="custom-policy-category"
                    style={{ width: '100%' }}
                  >
                    <option value="Custom">Custom</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Safeguarding">Safeguarding</option>
                    <option value="Welfare">Welfare</option>
                    <option value="Health & Safety">Health &amp; Safety</option>
                    <option value="Governance">Governance</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Ofsted reference (optional)</label>
                  <input
                    type="text"
                    value={customDraft.ofsted_reference}
                    onChange={(e) => setCustomDraft((s) => ({ ...s, ofsted_reference: e.target.value }))}
                    placeholder="—"
                    maxLength={60}
                    data-testid="custom-policy-ofsted"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                  Body (markdown) <span style={{ color: 'hsl(0 60% 40%)' }}>*</span>
                </label>
                <textarea
                  value={customDraft.body_markdown}
                  onChange={(e) => setCustomDraft((s) => ({ ...s, body_markdown: e.target.value }))}
                  placeholder="# Uniform Policy&#10;&#10;## 1. Purpose&#10;&#10;This policy sets out …"
                  rows={12}
                  data-testid="custom-policy-body"
                  style={{ width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.85rem', lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.85rem' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setCreatingCustom(false)} disabled={saving} data-testid="custom-policy-cancel">Cancel</button>
              <button type="button" className="btn btn-primary" onClick={submitCustom} disabled={saving} data-testid="custom-policy-save">
                {saving ? 'Creating…' : 'Create as draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PoliciesLibrary;
